import {
  AnimatedContainer,
  AnimatedPage,
  AnimatedSection
} from '@components/animated'
import CollapsibleAlert from '@components/collapsibleAlert/CollapsibleAlert'
import ConfirmationModal from '@components/confirmationModal/ConfirmationModal'
import { withDetailView } from '@components/enitity/detail/Detail.view'
import { Section } from '@components/index'
import { AccessControl } from '@components/layout'
import { useChatWidgetContext } from '@features/chat/context'
import { useUpdateStatus } from '@services/offers/mutations/useUpdateStatus'
import { useProfile } from '@services/users/queries/useProfile'
import { SNA_IMAGE_PREFIX } from '@services/utils'
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  CircleCheck,
  Eye,
  ExternalLink,
  FileText,
  Landmark,
  LayoutGrid,
  type LucideIcon,
  MapPin,
  Ruler,
  Wrench,
  X
} from 'lucide-react'
import { ReactNode, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { ImageLightbox } from '@components/image/ImageLightbox'
import type { FlatDetail, OfferDetail } from 'types/api'

import { FlatImage } from './FlatImage'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

const STATUS_TONE: Record<string, string> = {
  CREATED: 'bg-primary/15 text-primary',
  INTERESTED: 'bg-peach/30 text-warning',
  ACCEPTED: 'bg-primary/15 text-primary',
  RESERVED: 'bg-primary/15 text-primary',
  ONGOING: 'bg-primary/15 text-primary',
  REJECTED: 'bg-destructive/15 text-destructive',
  TERMINATED: 'bg-destructive/15 text-destructive',
  NOTICE_PERIOD: 'bg-peach/30 text-warning'
}

function DetailRow({
  icon: Icon,
  label,
  children
}: {
  icon: LucideIcon
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex items-start gap-3 text-[15px]">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 pt-0.5">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="font-semibold text-ink">{children}</div>
      </div>
    </div>
  )
}

type FlatsWidgetProps = {
  flat?: FlatDetail
  offer?: OfferDetail
}

function FlatsWidgetContent({ flat, offer }: FlatsWidgetProps) {
  const { openLastConversation } = useChatWidgetContext()
  const openChat = (data: OfferDetail) => {
    if (data?.offer_chats) {
      const lastIndex = data.offer_chats.length - 1
      data.offer_chats[lastIndex] &&
        openLastConversation(data.offer_chats[lastIndex])
    }
  }

  const { mutate: updateStatus } = useUpdateStatus(openChat)
  const { data: user } = useProfile(true)
  const { t } = useTranslation('flat')
  const navigate = useNavigate()
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [open, setOpen] = useState(false)
  const isRequiredDataFilled = Boolean(
    user?.address_number &&
      user?.last_name &&
      user?.birth_date &&
      user?.id_number &&
      user?.street &&
      user?.address_number &&
      user?.city &&
      user?.zip &&
      user?.country &&
      user?.nationality &&
      user?.phone &&
      user?.email
  )
  if (!flat) {
    return null
  }

  const handleInterested = () => {
    if (offer?.id)
      updateStatus({
        id: offer?.id,
        body: { status: 'INTERESTED', rent: offer.rent }
      })
  }

  const handleAccepted = () => {
    if (offer?.id)
      updateStatus({
        id: offer?.id,
        body: { status: 'ACCEPTED', rent: offer.rent }
      })
  }

  const handleReject = () => {
    setOpen(true)
  }

  const handleOk = () => {
    if (offer?.id)
      updateStatus({
        id: offer?.id,
        body: { status: 'CANCELED', rent: offer.rent }
      })
    setOpen(false)
  }

  const handleCancel = () => {
    setOpen(false)
  }

  const moveToContract = () => {
    navigate('/user/contract')
  }

  const status = offer?.status
  const tone = (status && STATUS_TONE[status]) || 'bg-ink/[0.07] text-ink/60'
  const gallery = flat.gallery ? [...flat.gallery].reverse() : []
  const photos = gallery.length
    ? gallery.map((g) => `${SNA_IMAGE_PREFIX}${g.id}`)
    : flat.main_image
      ? [`${SNA_IMAGE_PREFIX}${flat.main_image}`]
      : []
  const heroIndex = gallery.length
    ? Math.max(
        0,
        gallery.findIndex((g) => g.id === flat.main_image)
      )
    : 0
  const isUser = user?.role !== 'ROOMMATE'

  const showActions =
    user?.role === 'USER' &&
    status &&
    status !== 'CANCELED' &&
    status !== 'REJECTED'

  const actionButtons = showActions ? (
    <div className="flex flex-wrap gap-3">
        {status === 'ACCEPTED' ||
        status === 'RESERVED' ||
        status === 'ONGOING' ? (
          <Button onClick={moveToContract} disabled={status === 'ACCEPTED'}>
            <FileText className="h-4 w-4" />
            {t('detail.move')}
          </Button>
        ) : (
          <Button
            onClick={status === 'CREATED' ? handleInterested : handleAccepted}
            disabled={
              status === 'INTERESTED' &&
              (!offer?.has_inspection || !isRequiredDataFilled)
            }
          >
            <Check className="h-4 w-4" />
            {status === 'INTERESTED'
              ? t('detail.accept_offer')
              : t('detail.interested')}
          </Button>
        )}
        {status !== 'ONGOING' && (
          <Button
            variant="ghost"
            onClick={handleReject}
            className="border border-destructive/30 bg-transparent text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-4 w-4" />
            {t('detail.decline')}
          </Button>
        )}
    </div>
  ) : null

  return (
    <AnimatedPage>
      <ErrorBoundary fallback={<div>Error</div>}>
        <ConfirmationModal
          open={open}
          title={t('detail.modal.title')}
          text={t('detail.modal.description')}
          cancelText={t('detail.modal.return')}
          okText={t('detail.decline')}
          onOk={handleOk}
          onCancel={handleCancel}
        />

        <div className="mx-auto w-full max-w-[1100px]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <AccessControl role="USER">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  aria-label={t('common.back', { ns: 'layout' })}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/15 bg-white p-0 text-ink transition-colors hover:border-ink/30 hover:bg-ink/[0.03]"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              </AccessControl>
              <AccessControl role="USER">
                <nav className="flex min-w-0 items-center gap-1.5 text-sm">
                  <Link
                    to="/user/offer"
                    className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
                  >
                    {t('title', { ns: 'offers' })}
                  </Link>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink/30" />
                  <span className="truncate font-semibold text-ink">
                    {flat.title}
                  </span>
                </nav>
              </AccessControl>
            </div>
            <AccessControl role="ADMIN">
              {!location.pathname.includes('/flats/') && (
                <Button
                  variant="ghostDark"
                  onClick={() =>
                    window.open(`/admin/flats/${flat.id}`, '_blank')
                  }
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('navigation.show_flat')}
                </Button>
              )}
            </AccessControl>
          </div>

          <AnimatedContainer>
            <div className="space-y-5">
              {/* Hero gallery — bento grid */}
              <AnimatedSection>
                {(
                  <>
                    <div
                      className={cn(
                        'grid grid-cols-2 gap-2.5 sm:grid-cols-4',
                        gallery.length > 0 && 'sm:h-[440px] sm:grid-rows-2'
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => setLightboxIndex(heroIndex)}
                        className={cn(
                          'group relative h-60 overflow-hidden rounded-2xl border-0 p-0 sm:h-auto',
                          gallery.length > 0
                            ? 'col-span-2 sm:row-span-2'
                            : 'col-span-2 sm:col-span-4 sm:h-[440px]'
                        )}
                      >
                        <FlatImage
                          src={
                            flat.main_image
                              ? `${SNA_IMAGE_PREFIX}${flat.main_image}`
                              : undefined
                          }
                          alt={flat.title ?? ''}
                          className="h-full w-full rounded-2xl object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </button>

                      {gallery.slice(0, 4).map((image, idx) => {
                        const remaining = gallery.length - 4
                        const showMore = idx === 3 && remaining > 0
                        return (
                          <button
                            key={image.id}
                            type="button"
                            onClick={() => setLightboxIndex(idx)}
                            className="group relative h-28 overflow-hidden rounded-2xl border-0 p-0 sm:h-auto"
                          >
                            <FlatImage
                              src={`${SNA_IMAGE_PREFIX}${image.id}`}
                              alt=""
                              loading="lazy"
                              className="h-full w-full rounded-2xl object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            {showMore && (
                              <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-ink/55 text-lg font-bold text-white transition-colors group-hover:bg-ink/65">
                                +{remaining}
                              </div>
                            )}
                          </button>
                        )
                      })}
                    </div>

                    {lightboxIndex !== null && photos.length > 0 && (
                      <ImageLightbox
                        images={photos}
                        index={lightboxIndex}
                        onClose={() => setLightboxIndex(null)}
                        onIndexChange={setLightboxIndex}
                      />
                    )}
                  </>
                )}
              </AnimatedSection>

              {/* Alerts — right under the gallery */}
              {isUser && (
                <>
                  {!isRequiredDataFilled && offer?.has_inspection && (
                    <AnimatedSection>
                      <CollapsibleAlert
                        isExpanded
                        type="warning"
                        label={t('detail.alerts.labels.fill_mandatory')}
                        description={
                          <>
                            {t('detail.alerts.descriptions.fill_mandatory')}{' '}
                            <Link
                              to="/user/profile"
                              className="font-semibold text-primary underline underline-offset-2 hover:opacity-80"
                            >
                              {t('detail.profileLink')}
                            </Link>
                          </>
                        }
                      />
                    </AnimatedSection>
                  )}
                  {((status === 'INTERESTED' && offer?.has_inspection) ||
                    status === 'RESERVED') && (
                    <AnimatedSection>
                      <CollapsibleAlert
                        isExpanded
                        type="success"
                        label={
                          status === 'RESERVED'
                            ? t('detail.alerts.labels.reserved')
                            : t('detail.alerts.labels.take')
                        }
                        description={
                          status === 'RESERVED'
                            ? t('detail.alerts.descriptions.reserved')
                            : t('detail.alerts.descriptions.take')
                        }
                      />
                    </AnimatedSection>
                  )}
                  {((status === 'INTERESTED' && !offer?.has_inspection) ||
                    (status === 'ACCEPTED' && offer?.has_inspection)) && (
                    <AnimatedSection>
                      <CollapsibleAlert
                        isExpanded
                        type="info"
                        label={
                          !offer?.has_inspection
                            ? t('detail.alerts.labels.agree')
                            : t('detail.alerts.labels.accepted')
                        }
                        description={
                          !offer?.has_inspection
                            ? t('detail.alerts.descriptions.agree')
                            : t('detail.alerts.descriptions.accepted')
                        }
                      />
                    </AnimatedSection>
                  )}
                </>
              )}

              {/* Title + price + CTA bar */}
              <AnimatedSection>
                <Card className="p-6 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-3">
                      <h1 className="m-0 font-display text-[clamp(1.5rem,2.6vw,2.25rem)] font-bold leading-tight tracking-tight text-ink">
                        {flat.title}
                      </h1>
                      <div className="flex flex-wrap items-center gap-2">
                        <AccessControl role="USER">
                          {status && (
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                                tone
                              )}
                            >
                              {t(`status.${status.toLowerCase()}`, {
                                ns: 'offers',
                                defaultValue: status
                              })}
                            </span>
                          )}
                        </AccessControl>
                        {offer && (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
                              offer.has_inspection
                                ? 'bg-primary/15 text-primary'
                                : 'bg-ink/[0.07] text-ink/60'
                            )}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            {t('detail.has_inspection')}:{' '}
                            {offer.has_inspection
                              ? t('detail.yes')
                              : t('detail.no')}
                          </span>
                        )}
                        <AccessControl role="USER">
                          {offer?.deadline &&
                            status &&
                            ['CREATED', 'INTERESTED', 'ACCEPTED'].includes(
                              status
                            ) && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-peach/30 px-3 py-1 text-xs font-semibold text-warning">
                                <CalendarDays className="h-3.5 w-3.5" />
                                {t('sections.deadline', { ns: 'offers' })}:{' '}
                                {new Date(offer.deadline).toLocaleDateString(
                                  'sk-SK'
                                )}
                              </span>
                            )}
                        </AccessControl>
                      </div>
                      {flat.location_name && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4 shrink-0 text-primary" />
                          {flat.location_name}
                        </div>
                      )}
                    </div>

                    <div className="shrink-0 sm:text-right">
                      <div className="font-display text-[clamp(1.875rem,3vw,2.5rem)] font-bold leading-none text-ink">
                        <AccessControl role="USER">{offer?.rent} €</AccessControl>
                        <AccessControl role={['ADMIN', 'LANDLORD']}>
                          {flat?.rent} €
                        </AccessControl>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-ink/60 sm:justify-end">
                        <span>
                          + {t('detail.deposit')}{' '}
                          <strong className="font-semibold text-ink/80">
                            {offer?.deposit_sum ?? flat?.deposit} &euro;
                          </strong>
                        </span>
                        {(() => {
                          const monthly =
                            offer?.monthly_payments != null
                              ? parseFloat(String(offer.monthly_payments))
                              : (Number(flat?.monthly_advance_prescription) ||
                                  0) +
                                (Number(flat?.gas) || 0) +
                                (Number(flat?.electricity) || 0) +
                                (Number(flat?.internet_tv) || 0)
                          return monthly > 0 ? (
                            <span>
                              + {t('detail.energy')}{' '}
                              <strong className="font-semibold text-ink/80">
                                {monthly} &euro;
                              </strong>
                            </span>
                          ) : (
                            <AccessControl role="USER">
                              <span>{t('detail.energy_included')}</span>
                            </AccessControl>
                          )
                        })()}
                      </div>
                    </div>
                  </div>

                  {actionButtons && (
                    <>
                      <div className="my-5 h-px w-full bg-ink/[0.06]" />
                      {actionButtons}
                    </>
                  )}
                </Card>
              </AnimatedSection>

              {/* Quick facts */}
              <AnimatedSection>
                <Card className="p-6">
                  <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
                    <AccessControl role="ADMIN">
                      <DetailRow icon={Landmark} label="IBAN">
                        <span className="break-all text-[13px]">
                          {flat.iban ?? '-'}
                        </span>
                      </DetailRow>
                    </AccessControl>
                    <DetailRow icon={Ruler} label={t('detail.area')}>
                      {flat.area ? (
                        <>
                          {flat.area} m<sup>2</sup>
                        </>
                      ) : (
                        '—'
                      )}
                    </DetailRow>
                    {flat.room_count && (
                      <DetailRow icon={LayoutGrid} label={t('detail.room_count')}>
                        {flat.room_count}
                      </DetailRow>
                    )}
                    {flat.floor && (
                      <DetailRow icon={Building2} label={t('detail.floor')}>
                        {flat.floor}
                      </DetailRow>
                    )}
                    {flat.availability_date && (
                      <DetailRow
                        icon={CalendarDays}
                        label={t('detail.availability_date')}
                      >
                        {new Date(flat.availability_date).toLocaleDateString(
                          'sk-SK'
                        )}
                      </DetailRow>
                    )}
                    {flat.condition && (
                      <DetailRow icon={Wrench} label={t('detail.condition')}>
                        {t(`conditions.${flat.condition.toLowerCase()}`)}
                      </DetailRow>
                    )}
                  </div>
                </Card>
              </AnimatedSection>

              {flat.description && (
                <AnimatedSection>
                  <Section
                    title={
                      <span className="font-display text-base font-bold text-ink">
                        {t('detail.description')}
                      </span>
                    }
                    defaultActiveKey={['0']}
                  >
                    <div
                      className={cn(
                        'max-w-none text-[15px] leading-relaxed text-ink/80',
                        '[&>*+*]:mt-3 [&_p]:m-0',
                        '[&_h1]:mb-1.5 [&_h1]:mt-5 [&_h1]:font-display [&_h1]:text-lg [&_h1]:font-bold [&_h1]:text-ink',
                        '[&_h2]:mb-1.5 [&_h2]:mt-5 [&_h2]:font-display [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-ink',
                        '[&_h3]:mb-1.5 [&_h3]:mt-5 [&_h3]:font-display [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-ink',
                        '[&_strong]:font-semibold [&_strong]:text-ink',
                        '[&_a]:font-medium [&_a]:text-primary [&_a]:underline',
                        '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1',
                        '[&_img]:rounded-xl'
                      )}
                      dangerouslySetInnerHTML={{ __html: flat.description }}
                    />
                  </Section>
                </AnimatedSection>
              )}

              {flat.rooms && flat.rooms[0]?.equipment.length! > 0 && (
                <AnimatedSection>
                  <Section
                    title={
                      <span className="font-display text-base font-bold text-ink">
                        {t('detail.equipment')}
                      </span>
                    }
                    defaultActiveKey={['0']}
                  >
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                      {flat.rooms?.map(
                        (room) =>
                          room.equipment.length > 0 && (
                            <div key={room.id}>
                              <h4 className="mb-3 mt-0 font-display text-sm font-bold text-ink">
                                {room.room_type.name}
                              </h4>
                              <div className="flex flex-col gap-2">
                                {room.equipment.map((eq) => (
                                  <div
                                    key={eq.id}
                                    className="flex items-center gap-2 text-sm text-ink/80"
                                  >
                                    <CircleCheck className="h-4 w-4 shrink-0 text-primary" />
                                    {eq.name}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                      )}
                    </div>
                  </Section>
                </AnimatedSection>
              )}

              {flat.civic_facilities && flat.civic_facilities.length > 0 && (
                <AnimatedSection>
                  <Section
                    title={
                      <span className="font-display text-base font-bold text-ink">
                        {t('detail.civic_facilities')}
                      </span>
                    }
                    defaultActiveKey={['0']}
                  >
                    <div className="flex flex-wrap gap-x-8 gap-y-2.5">
                      {flat.civic_facilities?.map((cf) => (
                        <div
                          key={cf.id}
                          className="flex items-center gap-2 text-sm text-ink/80"
                        >
                          <CircleCheck className="h-4 w-4 shrink-0 text-primary" />
                          {cf.name}
                        </div>
                      ))}
                    </div>
                  </Section>
                </AnimatedSection>
              )}

              {flat.services && flat.services.length > 0 && (
                <AnimatedSection>
                  <Section
                    title={
                      <span className="font-display text-base font-bold text-ink">
                        {t('detail.services')}
                      </span>
                    }
                    defaultActiveKey={['0']}
                  >
                    <div className="flex flex-wrap gap-x-8 gap-y-2.5">
                      {flat.services?.map((service) => (
                        <div
                          key={service.id}
                          className="flex items-center gap-2 text-sm text-ink/80"
                        >
                          <CircleCheck className="h-4 w-4 shrink-0 text-primary" />
                          {service.name}
                        </div>
                      ))}
                    </div>
                  </Section>
                </AnimatedSection>
              )}
            </div>
          </AnimatedContainer>
        </div>
      </ErrorBoundary>
    </AnimatedPage>
  )
}

export const FlatsWidget = withDetailView(FlatsWidgetContent)
